import { create } from 'zustand';
import { api } from '../utils/api';

export type NotificationSeverity = 'critical' | 'warning' | 'info';
export type NotificationType =
  | 'security'
  | 'rotation'
  | 'mfa'
  | 'device'
  | 'secret'
  | 'system'
  | 'approval'
  | 'mention';

export interface NotificationAction {
  label: string;
  href?: string;
  onClick?: () => void;
}

export interface Notification {
  id: string;
  type: NotificationType;
  severity: NotificationSeverity;
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
  action?: NotificationAction;
  /** REQ-055: id persistido en el servidor (notificación interna por usuario); evita duplicados al sondear. */
  serverId?: string;
}

interface NotificationState {
  notifications: Notification[];
  isOpen: boolean;
  pollInterval: ReturnType<typeof setInterval> | null;

  // Actions
  addNotification: (notification: Omit<Notification, 'id' | 'read' | 'createdAt'>) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  clearAll: () => void;
  setOpen: (open: boolean) => void;
  toggleOpen: () => void;
  removeNotification: (id: string) => void;

  // Computed helpers
  unreadCount: () => number;
  getBySeverity: (severity: NotificationSeverity) => Notification[];

  // Polling
  startPolling: () => void;
  stopPolling: () => void;
  /** REQ-055: sondeo SOLO de las notificaciones personales (menciones); no toca el feed de alertas de administración. */
  startMentionPolling: () => () => void;
  fetchAlerts: () => Promise<void>;
  /** REQ-055: menciones en comentarios de tarea (feed personal persistido). */
  fetchMentions: () => Promise<void>;
}

const MAX_UNREAD = 100;
const AUTO_DISMISS_INFO_MS = 10_000;
const POLL_INTERVAL_MS = 30_000;

let idCounter = 0;
const generateId = () => `notif-${Date.now()}-${++idCounter}`;

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  isOpen: false,
  pollInterval: null,

  addNotification: (notification) => {
    const newNotif: Notification = {
      ...notification,
      id: generateId(),
      read: false,
      createdAt: new Date().toISOString(),
    };

    set((state) => {
      const notifications = [newNotif, ...state.notifications];
      // Cap unread at MAX_UNREAD
      const unread = notifications.filter((n) => !n.read);
      if (unread.length > MAX_UNREAD) {
        const toRead = unread.slice(MAX_UNREAD);
        return {
          notifications: notifications.map((n) =>
            toRead.find((r) => r.id === n.id) ? { ...n, read: true } : n
          ),
        };
      }
      return { notifications };
    });

    // Auto-dismiss info notifications after 10s
    if (notification.severity === 'info') {
      setTimeout(() => {
        get().removeNotification(newNotif.id);
      }, AUTO_DISMISS_INFO_MS);
    }
  },

  markRead: (id) => {
    const target = get().notifications.find((n) => n.id === id);
    if (target?.serverId && !target.read) void api.post(`/work/notifications/${target.serverId}/read`).catch(() => undefined);
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, read: true } : n
      ),
    }));
  },

  markAllRead: () => {
    if (get().notifications.some((n) => n.serverId && !n.read)) void api.post('/work/notifications/read-all').catch(() => undefined);
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, read: true })),
    }));
  },

  clearAll: () => set({ notifications: [] }),

  setOpen: (open) => set({ isOpen: open }),
  toggleOpen: () => set((state) => ({ isOpen: !state.isOpen })),

  removeNotification: (id) =>
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    })),

  unreadCount: () => get().notifications.filter((n) => !n.read).length,

  getBySeverity: (severity) =>
    get().notifications.filter((n) => n.severity === severity),

  startPolling: () => {
    const { pollInterval } = get();
    if (pollInterval) return; // already polling

    // Initial fetch
    get().fetchAlerts();
    get().fetchMentions();

    const interval = setInterval(() => {
      get().fetchAlerts();
      get().fetchMentions();
    }, POLL_INTERVAL_MS);

    set({ pollInterval: interval });
  },

  stopPolling: () => {
    const { pollInterval } = get();
    if (pollInterval) {
      clearInterval(pollInterval);
      set({ pollInterval: null });
    }
  },

  startMentionPolling: () => {
    void get().fetchMentions();
    const t = setInterval(() => void get().fetchMentions(), POLL_INTERVAL_MS);
    return () => clearInterval(t);
  },

  fetchAlerts: async () => {
    try {
      const response = await api.get<{ data: Notification[] }>('/admin/alerts');
      const { notifications } = get();
      const existingIds = new Set(notifications.map((n) => n.id));

      for (const alert of response.data.data) {
        if (!existingIds.has(alert.id)) {
          get().addNotification(alert);
        }
      }
    } catch {
      // Silently fail — polling will retry
    }
  },

  fetchMentions: async () => {
    try {
      const res = await api.get<{ id: string; kind: string; title: string; read: boolean; createdAt: string; href: string | null }[]>('/work/notifications');
      set((state) => {
        const seen = new Set(state.notifications.map((n) => n.serverId).filter(Boolean));
        const fresh = res.data
          .filter((r) => r.kind === 'TASK_MENTION' && !seen.has(r.id))
          .map((r): Notification => ({
            id: `srv-${r.id}`, serverId: r.id, type: 'mention', severity: 'info', title: 'Te han mencionado', message: r.title,
            read: r.read, createdAt: r.createdAt, ...(r.href ? { action: { label: 'Abrir comentario', href: r.href } } : {}),
          }));
        if (!fresh.length) return {};
        // Las menciones no se descartan solas: permanecen hasta que se lean.
        return { notifications: [...fresh, ...state.notifications] };
      });
    } catch {
      // Silencioso: el sondeo reintenta.
    }
  },
}));
